import React, { useState, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormContext } from 'react-hook-form';
import { Link, Redirect } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import MuiLink from '@material-ui/core/Link';
import { Auth } from 'aws-amplify';
import { useStyles } from './styles';
import UnathorizedLayout from '../../shared/layout/UnauthorizedLayout';
import FormInput from '../../shared/form/FormInput';
import { AlertContext } from '../../shared/app/AlertProvider';
import { FormBox } from '../../shared/form-box';
import { IStatus, IError, cognitoErrorType } from '../../../types';

const ForgotPassword = () => {
  const [status, setStatus] = useState<IStatus>({ value: 'idle', code: null });
  const [redirect, setRedirect] = useState(false);
  const { showAlert } = useContext(AlertContext);
  const classes = useStyles();

  const { t } = useTranslation();

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    submitFocusError: false,
  });

  const { handleSubmit, getValues } = methods;

  const onSubmit = useCallback(
    (data) => {
      const { email } = data;
      setStatus({ value: 'pending', code: null });
      Auth.forgotPassword(email)
        .then(() => {
          setRedirect(true);
          showAlert({
            severity: 'success',
            message: t(
              'v2.verification.snackbar_label_resetPasswordEmailSentOut',
            ),
          });
        })
        .catch((err: IError) => {
          if (err.__type === cognitoErrorType.USER_NOT_FOUND_EXCEPTION) {
            showAlert({
              severity: 'error',
              message: t('v2.global.snackbar_label_error_somethingWentWrong'),
            });
          } else {
            showAlert({
              severity: 'error',
              message: t('v2.global.snackbar_label_error_somethingWentWrong'),
            });
          }
          setStatus({ value: 'rejected', code: err.status });
        });
    },
    [showAlert, t],
  );

  if (redirect) {
    return (
      <Redirect
        to={{
          pathname: '/forgot-password/success',
          state: { email: getValues().email },
        }}
      />
    );
  }

  return (
    <UnathorizedLayout
      title={t('v2.forgotPassword.header_label_title')}
      subtitle={t('v2.forgotPassword.header_label_subTitle')}
    >
      <FormContext {...methods}>
        <FormBox
          component="form"
          p={3}
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormInput
                id="forgot-password-email"
                type="email"
                name="email"
                label={t('v2.global.textField_label_companyEmail')}
                placeholder={t('ForgotPassword.emailPlaceholder')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Box pt={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={status.value === 'pending'}
                >
                  {t('ForgotPassword.link')}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              {t('v2.global.label_createNewAccount')}
              <MuiLink
                component={Link}
                to="/registration"
                variant="body1"
                className={classes.links}
              >
                {t('v2.global.button_createNewAccount')}
              </MuiLink>
            </Grid>
            <Grid item xs={12}>
              {t('v2.global.label_needHelp')}
              <MuiLink
                component="a"
                variant="body1"
                className={classes.links}
                href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL}`}
              >
                {t('v2.global.button_needHelp')}
              </MuiLink>
            </Grid>
          </Grid>
        </FormBox>
      </FormContext>
    </UnathorizedLayout>
  );
};

export default ForgotPassword;
